import { create } from 'zustand';
import { isValidEmail } from '../helpers/regexp';
import {
  LoginRequest,
  RestoreRequest,
  login,
  restore,
  logout,
  refresh,
  changePassword,
  ChangePasswordRequest,
} from './api/auth-api';
import { useAlertStore } from './use-alert-store';

type ChangePasswordType = {
  repeatPassword: string;
} & ChangePasswordRequest;

interface Store {
  login: () => Promise<boolean>;
  restore: () => Promise<boolean>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  changePassword: () => Promise<void>;
  loginData?: Partial<LoginRequest>;
  restoreData?: Partial<RestoreRequest>;
  changePasswordData?: Partial<ChangePasswordType>;
  handleUpdateLoginData: (data: Partial<LoginRequest>) => void;
  handleUpdateRestoreData: (data: Partial<RestoreRequest>) => void;
  handleUpdateChangePasswordData: (data: Partial<ChangePasswordType>) => void;
  isLoading?: boolean;
  error?: string;
  resetError: () => void;
}

export const useAuthStore = create<Store>((set, get) => ({
  handleUpdateLoginData: async (data) => {
    try {
      set({ error: undefined });
      set((store) => ({ ...store, loginData: { ...store.loginData, ...data } }));
    } catch (e) {
      console.error(e);
    }
  },
  handleUpdateRestoreData: async (data) => {
    try {
      set((store) => ({
        ...store,
        restoreData: { ...store.restoreData, ...data },
        error: isValidEmail(data.email) ? undefined : 'Неверный email',
      }));
    } catch (e) {
      console.error(e);
    }
  },
  handleUpdateChangePasswordData: async (data) => {
    try {
      set((store) => ({
        ...store,
        changePasswordData: { ...store.changePasswordData, ...data },
      }));
    } catch (e) {
      console.error(e);
    }
  },
  login: async () => {
    set({ isLoading: true });
    let isAdmin = false;
    try {
      const req = get().loginData;
      if (!req) {
        throw new Error('Login data is missing');
      }
      const resp = await login(req);
      isAdmin = !!resp?.data.is_admin;
    } catch (e) {
      set({ error: 'Неверный логин или пароль' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
    return isAdmin;
  },
  restore: async () => {
    set({ isLoading: true });
    let result = true;
    try {
      const req = get().restoreData;
      if (!req) {
        throw new Error('Restore data is missing');
      }
      await restore(req);
      useAlertStore
        .getState()
        .add({ label: 'Письмо для восстановления отправлено', variant: 'success' });
    } catch (e) {
      set({ error: 'Неверный email' });
      result = false;
      throw e;
    } finally {
      set({ isLoading: false });
    }
    return result;
  },
  logout: async () => {
    set({ isLoading: true });
    try {
      await logout();
    } catch (e) {
      console.error(e);
    } finally {
      set({ isLoading: false });
    }
  },
  refresh: async () => {
    set({ isLoading: true });
    try {
      await refresh();
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  changePassword: async () => {
    set({ isLoading: true });
    try {
      const data = get().changePasswordData;
      if (!data?.password) {
        throw new Error('New pass data is missing');
      }
      if (data?.password !== data?.repeatPassword) {
        useAlertStore.getState().add({ label: 'Пароли не совпадают', variant: 'danger' });
        return;
      }

      await changePassword(data);
      const reInitData = { login: data.login };
      set({ changePasswordData: reInitData });
      useAlertStore.getState().add({ label: 'Пароль успешно изменен', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Ошибка при смене пароля', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  resetError: async () => {
    try {
      set((store) => ({
        ...store,
        error: undefined,
      }));
    } catch (e) {
      console.error(e);
    }
  },
}));
